import React, { useState, useEffect, useRef } from 'react';
import { Handle, Position } from 'reactflow';
import { usePipeline } from '../PipelineContext';

const TRIGGER_LABELS = {
  manual:              { icon: '▶',  text: 'Manual run' },
  record_added:        { icon: '➕', text: 'Record added' },
  field_change:        { icon: '✏️', text: 'Field changed' },
  function_task_change:{ icon: '📋', text: 'Task changed' },
  task_status_change:  { icon: '📌', text: 'Project task status' },
  schedule:            { icon: '🕐', text: 'Scheduled' },
  poll_api:            { icon: '🔄', text: 'Polling API' },
  webhook:             { icon: '🪝', text: 'Webhook' },
};

function ScheduleCountdown({ intervalSec, active }) {
  const [remaining, setRemaining] = useState(intervalSec);
  const startRef = useRef(Date.now());

  useEffect(() => {
    if (!active) { setRemaining(intervalSec); return; }
    startRef.current = Date.now();
    setRemaining(intervalSec);
    const tick = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startRef.current) / 1000);
      const left = intervalSec - (elapsed % intervalSec);
      setRemaining(left);
    }, 1000);
    return () => clearInterval(tick);
  }, [active, intervalSec]);

  if (!active) return null;

  const fmt = remaining >= 60
    ? `${Math.floor(remaining / 60)}m ${remaining % 60}s`
    : `${remaining}s`;

  return <span className="trigger-countdown">Next in {fmt}</span>;
}

export default function TriggerNode({ id, data, selected }) {
  const [showInfo, setShowInfo] = useState(false);
  const pipeline = usePipeline();
  const { opsMode, pipelineActive, onRunWorkflow } = pipeline || {};

  const t = TRIGGER_LABELS[data.triggerType || 'manual'] || TRIGGER_LABELS.manual;
  const headerStyle = data.color ? { background: data.color + '22', color: data.color } : {};
  const nodeStyle   = data.color ? { borderColor: data.color } : {};

  const intervalSec = data.triggerType === 'schedule'
    ? Math.max(parseInt(data.scheduleInterval || '60', 10), 10)
    : 0;

  return (
    <div className={`pipeline-node trigger-node ${selected ? 'selected' : ''}`} style={nodeStyle}>
      <Handle type="target" position={Position.Left} className="node-handle" />

      <div className="node-header trigger-header" style={headerStyle}>
        <span className="node-type-icon">⚡</span>
        <span className="node-type-label">{opsMode ? 'Automation Start' : 'Trigger'}</span>
        <button
          className="node-info-btn"
          onMouseDown={e => e.stopPropagation()}
          onClick={e => { e.stopPropagation(); setShowInfo(s => !s); }}
        >ⓘ</button>
      </div>

      <div className="node-body">
        <div className="node-title">{data.label || 'Trigger'}</div>
        {data.description && <div className="node-description">{data.description}</div>}

        {showInfo && (
          <div className="node-info-card">
            <div className="node-info-what">Starts a workflow. Connect it to Condition, Function, or Action nodes to define the flow.</div>
            <div className="node-info-eg-label">Trigger types:</div>
            <div className="node-info-eg">→ Manual — user presses Run</div>
            <div className="node-info-eg">→ Record/field — react to data changes</div>
            <div className="node-info-eg">→ Task change — react to function task updates</div>
            <div className="node-info-eg">→ Schedule — run on a timer</div>
            <div className="node-info-eg">→ Poll API / Webhook — respond to external events</div>
          </div>
        )}

        <div className="trigger-type-chip">
          <span className="trigger-chip-icon">{t.icon}</span>
          <span>{t.text}</span>
        </div>

        {data.triggerType === 'schedule' && intervalSec > 0 && (
          <div className="trigger-schedule-info">
            Every {intervalSec >= 60
              ? `${Math.round(intervalSec / 60)}m`
              : `${intervalSec}s`
            }
            <ScheduleCountdown intervalSec={intervalSec} active={!!pipelineActive} />
          </div>
        )}

        {(data.triggerType === 'manual' || !data.triggerType) && (
          <button
            className="trigger-run-btn"
            onMouseDown={e => e.stopPropagation()}
            onClick={e => { e.stopPropagation(); onRunWorkflow?.(id); }}
          >
            ▶ Run workflow
          </button>
        )}
      </div>

      <Handle type="source" position={Position.Right} className="node-handle" />
    </div>
  );
}
